import React, { useState } from "react";
import { Typography, Chip, Box, useTheme } from "@mui/material";
import "./index.css";
import TextInput from "../../../components/TextInput";
import { useDispatch, useSelector } from "react-redux";
import { setOnboarding } from "../../../rtkSlices/onboardingSlice";
import { RootState } from "../../../store";

const WorkAndPersonality = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const onboarding = useSelector(
    (state: RootState) => state.onboarding.onboarding
  );
  const workOrStudyStatus: string[] = onboarding?.workOrStudyStatus ?? [];
  const fetchedPersonalities: string[] = onboarding && onboarding.personality;
  const [workSelections, setWorkSelections] = useState<
    {
      id: number;
      name: string;
      selected: boolean;
    }[]
  >([
    {
      id: 1,
      name: "Student (High School)",
      selected: workOrStudyStatus.includes("Student (High School)"),
    },
    {
      id: 2,
      name: "Student (College/University)",
      selected: workOrStudyStatus.includes("Student (College/University)"),
    },
    {
      id: 3,
      name: "Freelancer or Consultant",
      selected: workOrStudyStatus.includes("Freelancer or Consultant"),
    },
    {
      id: 4,
      name: "Full-time employee",
      selected: workOrStudyStatus.includes("Full-time employee"),
    },
    {
      id: 5,
      name: "Entrepreneur or Business Owner",
      selected: workOrStudyStatus.includes("Entrepreneur or Business Owner"),
    },
    {
      id: 6,
      name: "Currently Job Seeking",
      selected: workOrStudyStatus.includes("Currently Job Seeking"),
    },
    {
      id: 7,
      name: "Retired / Not working",
      selected: workOrStudyStatus.includes("Retired / Not working"),
    },
    {
      id: 8,
      name: "Any other thing",
      selected: workOrStudyStatus.includes("Any other thing"),
    },
  ]);
  const MAX_OTHER_WORK_LEN = 160;
  const [otherWorkText, setOtherWorkText] = useState(
    workOrStudyStatus.find(
      (w) => !workSelections.map((ws) => ws.name).includes(w)
    ) || ""
  );
  const [personalities, setPersonalities] = useState([
    {
      id: 1,
      name: "Introverted / Reserved",
      selected: fetchedPersonalities.includes("Introverted / Reserved"),
    },
    {
      id: 2,
      name: "Extroverted / Outgoing",
      selected: fetchedPersonalities.includes("Extroverted / Outgoing"),
    },
    {
      id: 3,
      name: "Creative / Artistic",
      selected: fetchedPersonalities.includes("Creative / Artistic"),
    },
    {
      id: 4,
      name: "Analytical / Logical",
      selected: fetchedPersonalities.includes("Analytical / Logical"),
    },
    {
      id: 5,
      name: "Practical / Hands-on",
      selected: fetchedPersonalities.includes("Practical / Hands-on"),
    },
    {
      id: 6,
      name: "Empathetic / Caring",
      selected: fetchedPersonalities.includes("Empathetic / Caring"),
    },
    {
      id: 7,
      name: "Curious / Explorative",
      selected: fetchedPersonalities.includes("Curious / Explorative"),
    },
    {
      id: 8,
      name: "Organized / Detail-oriented",
      selected: fetchedPersonalities.includes("Organized / Detail-oriented"),
    },
    {
      id: 9,
      name: "Flexible / Spontaneous",
      selected: fetchedPersonalities.includes("Flexible / Spontaneous"),
    },
  ]);

  const selectWork = (id: number) => {
    let works = [...workSelections];
    const selectedWork = works.find((work: { id: number }) => work.id == id);
    if (
      works.findIndex((work: { id: number }) => work.id == id) != -1 &&
      selectedWork
    ) {
      works[works.findIndex((work: { id: number }) => work.id == id)].selected =
        !selectedWork.selected;
    }
    setWorkSelections(works);
    dispatch(
      setOnboarding({
        workOrStudyStatus: works
          .filter((work) => work.selected)
          .map((work) => work.name),
      })
    );
  };

  const handleOtherWork = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setOtherWorkText(newText);

    const tempOnboarding = JSON.parse(JSON.stringify(onboarding));
    const idx = tempOnboarding.workOrStudyStatus.findIndex(
      (work: string) => !workSelections.map((w) => w.name).includes(work)
    );

    if (idx !== -1) tempOnboarding.workOrStudyStatus[idx] = newText;
    else tempOnboarding.workOrStudyStatus.push(newText);

    dispatch(setOnboarding(tempOnboarding));
  };
  const selectPersonality = (id: number) => {
    let tempPersonalities = [...personalities];
    const selectedPersonality = tempPersonalities.find(
      (work: { id: number }) => work.id == id
    );
    if (
      tempPersonalities.findIndex((work: { id: number }) => work.id == id) !=
        -1 &&
      selectedPersonality
    ) {
      tempPersonalities[
        tempPersonalities.findIndex((work: { id: number }) => work.id == id)
      ].selected = !selectedPersonality.selected;
    }
    setPersonalities(tempPersonalities);
    dispatch(
      setOnboarding({
        personality: personalities
          .filter((work) => work.selected)
          .map((work) => work.name),
      })
    );
  };

  return (
    <Box className="work-section">
      <Typography component="div" fontSize={"53px"} fontFamily={"Fredoka"} fontWeight={"600"}>
        Work & Personality
      </Typography>
      <Typography component="div" fontSize={"16px"} fontFamily={"Poppins"} fontWeight={"300"}>
        Tell me about yourself
      </Typography>
      <br />
      <br />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          flexDirection: "column",
        }}
      >
        <Typography component="div" fontSize={"18px"} fontFamily={"Poppins"} fontWeight={"600"}>
          What is your work or study situation?
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            borderRadius: 2,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {workSelections.map((selection) => {
            return (
              <Chip
                label={selection.name}
                variant="outlined"
                onClick={() => selectWork(selection.id)}
                sx={{
                  margin: "7px",
                  marginLeft: "0px",
                  borderColor: "rgba(42, 148, 95, 1)",
                }}
                className={selection.selected ? "selected-option" : ""}
              />
            );
          })}
        </Box>
      </Box>
      <br />
      <Box sx={{ width: "100%" }}>
        <TextInput
          label={"Additional work/study context"}
          width="360px"
          value={otherWorkText}
          maxLength={MAX_OTHER_WORK_LEN}
          helperText={`${otherWorkText.length}/${MAX_OTHER_WORK_LEN}`}
          handleChange={handleOtherWork}
          color={theme.palette.primary.main}
        />
      </Box>
      <br />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          flexDirection: "column",
        }}
      >
        <Typography component="div" fontSize={"18px"} fontFamily={"Poppins"} fontWeight={"600"}>
          How would you describe your personality?
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            borderRadius: 2,
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          {personalities.map((selection) => {
            return (
              <Chip
                label={selection.name}
                variant="outlined"
                onClick={() => selectPersonality(selection.id)}
                sx={{
                  margin: "7px",
                  marginLeft: "0px",
                  borderColor: "rgba(42, 148, 95, 1)",
                }}
                className={selection.selected ? "selected-option" : ""}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkAndPersonality;
